import * as Sentry from '@sentry/react';

// Initialize Sentry
export function initSentry() {
  // Only initialize in production or when DSN is provided
  if (import.meta.env.PROD || import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN || "https://d4e4f6e03880d63ec8d8e0cb36f93c9d@o4509786333184000.ingest.de.sentry.io/4509808824221776",
      // Setting this option to true will send default PII data to Sentry.
      sendDefaultPii: true,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      // Performance Monitoring - 10% in production, 100% in development
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      // Session Replay - 10% in production, 100% in development
      replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      // Capture 100% of sessions with errors
      replaysOnErrorSampleRate: 1.0,
      // Environment
      environment: import.meta.env.MODE,
      // Release version
      release: import.meta.env.VITE_APP_VERSION || '1.0.0',
      // Debug mode in development
      debug: import.meta.env.DEV,
      // Before send hook to filter out certain errors
      beforeSend(event, hint) {
        // Don't send errors in development unless explicitly enabled
        if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DEBUG) {
          return null;
        }
        
        // Filter out certain types of errors
        if (event.exception) {
          const exception = event.exception.values?.[0];
          if (exception?.type === 'ChunkLoadError') {
            // Don't send chunk load errors as they're usually network issues
            return null;
          }
        }
        
        // Downgrade FEATURE_DISABLED errors to INFO level to prevent spam
        // These are expected when Microsoft integration is disabled
        if (event.extra?.code === 'FEATURE_DISABLED' || 
            (event.exception?.values?.[0]?.value?.includes('FEATURE_DISABLED')) ||
            (hint.originalException && (hint.originalException as any)?.code === 'FEATURE_DISABLED')) {
          event.level = 'info';
          // Add context that this is expected behavior
          event.tags = {
            ...event.tags,
            feature_disabled: true,
            expected_behavior: true
          };
        }
        
        return event;
      },
      // Attach stack traces to errors
      attachStacktrace: true,
      // Send errors even if they're caught by error boundaries
      autoSessionTracking: true,
    });
  }
}

// Export Sentry for use in components
export { Sentry };

// Helper function to capture errors
export function captureError(error: Error, context?: Record<string, any>) {
  if (import.meta.env.PROD || import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(error, {
      extra: context,
    });
  } else {
    // In development, just log to console
    console.error('Sentry would capture:', error, context);
  }
}

// Helper function to capture messages
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  if (import.meta.env.PROD || import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureMessage(message, level);
  } else {
    // In development, just log to console
    console.log(`Sentry would capture message (${level}):`, message);
  }
}

// Helper function to set user context
export function setUser(user: { id?: string; email?: string; username?: string }) {
  if (import.meta.env.PROD || import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setUser(user);
  }
}

// Helper function to clear user context
export function clearUser() {
  if (import.meta.env.PROD || import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setUser(null);
  }
}
